import React, { useState, useEffect, useMemo } from "react";
import { Calendar, Users, Clock, ChevronDown, Tag, Phone, Mail, Loader2, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import axios from "axios";

const RESTAURANT_PHONE = "06 32 18 35 54";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Créneaux disponibles selon le jour d'ouverture
// Lundi-Samedi : 19:00 - 22:30 (dernier créneau 21:30, dernier service ~22:30)
// Dimanche : 12:00 - 14:30 (dernier créneau 13:30)
const DINNER_SLOTS = ["19:00", "19:30", "20:00", "20:30", "21:00", "21:30"];
const LUNCH_SLOTS = ["12:00", "12:30", "13:00", "13:30"];

const getSlotsForDate = (isoDate) => {
  if (!isoDate) return DINNER_SLOTS;
  // "yyyy-mm-dd" — parse en UTC pour éviter les décalages fuseau, puis extraire le jour
  const [y, m, d] = isoDate.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay(); // 0 = dimanche, 1 = lundi, ...
  return dow === 0 ? LUNCH_SLOTS : DINNER_SLOTS;
};

const getDayLabel = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return dow === 0 ? "12:00 – 14:30 (déjeuner)" : "19:00 – 22:30 (dîner)";
};

const ReservationCard = () => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("20:00");
  const [guests, setGuests] = useState(2);
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {ok: bool, msg}

  const times = useMemo(() => getSlotsForDate(date), [date]);
  const dayLabel = useMemo(() => getDayLabel(date), [date]);

  useEffect(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    setDate(t.toISOString().split("T")[0]);
  }, []);

  // Ajuste automatiquement l'heure si la date change et l'ancienne heure n'est plus valide
  useEffect(() => {
    if (times.length && !times.includes(time)) {
      // Choisit un créneau par défaut : 20:00 le soir, 12:30 le midi
      setTime(times.includes("20:00") ? "20:00" : times.includes("12:30") ? "12:30" : times[0]);
    }
  }, [times, time]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!expanded) {
      setExpanded(true);
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/reservations`, {
        name,
        email,
        phone: phone || null,
        guests,
        date,
        time,
        message: message || null,
      });
      setResult({
        ok: true,
        msg: data.emails_sent
          ? `Merci ${data.name} ! Votre demande pour ${data.guests} pers. le ${data.date} à ${data.time} a bien été enregistrée. Un e-mail de confirmation vient d'être envoyé à ${data.email}.`
          : `Merci ${data.name} ! Votre demande pour ${data.guests} pers. le ${data.date} à ${data.time} a bien été enregistrée. Le restaurant vous contactera très prochainement au ${phone || "numéro fourni"} ou par e-mail pour confirmer.`,
      });
      setName(""); setEmail(""); setPhone(""); setMessage("");
      setExpanded(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setResult({
        ok: false,
        msg: typeof detail === "string" ? detail : "Une erreur est survenue, veuillez réessayer ou nous appeler directement.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="reservation" className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 lg:sticky lg:top-24">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xl font-bold text-gray-900">Réservez une table</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">Gratuitement · réponse sous 24 h</p>

      <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-[#E6F3F1] rounded-lg">
        <Tag className="h-4 w-4 text-[#1B7B71]" />
        <span className="text-sm font-semibold text-[#1B7B71]">Confirmation immédiate · sans frais</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="relative">
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          <select value={guests} onChange={(e) => setGuests(Number(e.target.value))}
            className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#1B7B71]">
            {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n} personne{n>1?"s":""}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        </div>

        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none z-10" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="block w-full min-w-0 pl-10 pr-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#1B7B71]"
            style={{ WebkitAppearance: "none", MozAppearance: "none", minHeight: "44px", boxSizing: "border-box" }}
          />
        </div>

        <div className="relative">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          <select value={time} onChange={(e) => setTime(e.target.value)}
            className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#1B7B71]">
            {times.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        </div>

        {dayLabel && (
          <p className="flex items-center gap-1.5 text-xs text-gray-500 -mt-1">
            <AlertTriangle className="h-3.5 w-3.5 text-[#1B7B71]" />
            Service ce jour : {dayLabel}
          </p>
        )}

        <div className="grid grid-cols-4 gap-2 pt-1">
          {times.slice(0, 4).map((t) => (
            <button type="button" key={t} onClick={() => setTime(t)}
              className={`py-2 text-xs font-semibold rounded-md border transition-colors ${time===t?"bg-[#1B7B71] text-white border-[#1B7B71]":"bg-white text-[#1B7B71] border-[#1B7B71] hover:bg-[#1B7B71]/5"}`}>
              {t}
            </button>
          ))}
        </div>

        {expanded && (
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <input required type="text" placeholder="Votre nom" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71]" />
            <input required type="email" placeholder="Votre e-mail" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71]" />
            <input type="tel" placeholder="Votre téléphone (optionnel)" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71]" />
            <textarea placeholder="Message ou demande spéciale" rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71] resize-none" />
          </div>
        )}

        <button type="submit" disabled={loading}
          className="w-full py-3 bg-[#1B7B71] text-white font-semibold rounded-lg hover:bg-[#155f57] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {expanded ? "Confirmer la réservation" : "Réserver en ligne"}
        </button>

        {result && (
          <div className={`flex items-start gap-2 text-sm rounded-lg p-3 ${result.ok ? "bg-[#E6F3F1] text-[#155f57] border border-[#1B7B71]/20" : "bg-red-50 text-red-700 border border-red-200"}`}>
            {result.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            <span>{result.msg}</span>
          </div>
        )}
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-500 uppercase tracking-wider">ou</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <a href={`tel:${RESTAURANT_PHONE.replace(/\s/g, "")}`}
        className="flex items-center justify-center gap-2 w-full py-3 border-2 border-[#1B7B71] text-[#1B7B71] font-semibold rounded-lg hover:bg-[#1B7B71] hover:text-white transition-colors">
        <Phone className="h-4 w-4" /> Réserver par téléphone
      </a>
      <a href={`tel:${RESTAURANT_PHONE.replace(/\s/g, "")}`} className="block text-center text-sm text-gray-600 hover:text-[#1B7B71] mt-1">
        {RESTAURANT_PHONE}
      </a>

      <a href="mailto:lchainoy27@gmail.com"
        className="flex items-center justify-center gap-2 w-full py-3 mt-3 border-2 border-[#1B7B71] text-[#1B7B71] font-semibold rounded-lg hover:bg-[#1B7B71] hover:text-white transition-colors">
        <Mail className="h-4 w-4" /> Nous écrire un e-mail
      </a>

      <p className="text-xs text-gray-500 mt-4 text-center">Annulation gratuite · Réponse sous 24 h</p>
    </div>
  );
};

export default ReservationCard;
